import React, { useContext, useEffect, useState } from 'react';
import Modal from 'react-modal';
import { style } from 'typestyle';
import { useTranslation } from 'react-i18next';
import RoomListCard from '../cards/room-list-card.component';
import OfficeFormType from '../enums/office-form-types';
import Department from '../models/department/department';
import type DepartmentChangeData from '../models/department/department-change-data';
import type Room from '../models/room/room';
import * as styles from './modal.styles';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import Grid from '../../../core/components/grid/grid.component';

interface Props {
  readonly showModal: boolean;
  readonly handleClose: () => void;
  readonly rooms: Room[];
  readonly handleCreate: (departmentChangeData: DepartmentChangeData) => void;
}
const CreateDepartmentModal = ({ showModal, handleClose, rooms, handleCreate }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const [department, setDepartment] = useState<Department>(new Department());
  const [selectedRooms, setSelectedRooms] = useState<Room[]>([]);
  const sortedRooms = rooms.sort((a, b) => (a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1));
  const resetModalState = (): void => {
    setSelectedRooms([]);
    setDepartment(new Department());
  };

  const handleCloseModal = (): void => {
    resetModalState();
    handleClose();
  };

  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!showModal) {
      setIsLoading(false);
    }
  }, [showModal]);

  const handleSave = (): void => {
    setIsLoading(true);
    resetModalState();
    handleCreate({ department, rooms: selectedRooms });
  };

  const handleDepartmentChange = (officeFormType: OfficeFormType, departmentName: string): void => {
    const departmentCopy = { ...department };
    switch (officeFormType) {
      case OfficeFormType.name:
        departmentCopy.name = departmentName;
        break;
      default:
        break;
    }
    setDepartment(departmentCopy);
  };

  const handleRoomSelected = (selected: Room): void => {
    let selectedRoomsCopy: Room[] = Object.assign([], selectedRooms);
    if (selectedRoomsCopy.some((room) => room.id === selected.id)) {
      selectedRoomsCopy = selectedRoomsCopy.filter((room) => room.id !== selected.id);
    } else {
      selectedRoomsCopy.push(selected);
    }
    setSelectedRooms(selectedRoomsCopy);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleCloseModal} style={styles.modalStyle(isMobile, 800)}>
      <Column className={style(styles.modalBody(isMobile))} crossAxisAlignment={CrossAxisAlignment.Stretch}>
        <span className={style(styles.columnHeader(isMobile, 8))}>{t('create-new-department')}</span>
        <LabeledInput
          labelText={t('name').toString()}
          value={department.name}
          onChange={(value): void => {
            handleDepartmentChange(OfficeFormType.name, value);
          }}
          onEnter={(): void => {
            handleSave();
          }}
        />
        <span className={style(styles.modalText)}>{t('rooms-in-department')}</span>
        {sortedRooms.length === 0 ? (
          <span className={style(styles.emptyListMessage)}>{t('no-rooms-available-message')}</span>
        ) : (
          <Grid
            rowClass={style(styles.gridRow)}
            itemSpacing={16}
            numberOfColumns={3}
            numberOfItems={sortedRooms.length}
            renderItem={(index): React.JSX.Element => {
              const roomItem = sortedRooms[index];
              return (
                roomItem && (
                  <div
                    className={isMobile ? style(styles.rowItem(true)) : undefined}
                    key={roomItem.id}
                    onClick={(): void => {
                      handleRoomSelected(roomItem);
                    }}
                    role="presentation"
                  >
                    <RoomListCard room={roomItem} checked={selectedRooms.some((room) => room.id === roomItem.id)} />
                  </div>
                )
              );
            }}
          />
        )}
        <Row itemSpacing={12} mainAxisAlignment={MainAxisAlignment.Stretch}>
          <div className={style(styles.button)}>
            <PrimaryButton
              fullWidth
              onClick={(): void => {
                handleSave();
              }}
              loading={isLoading}
            >
              {t('create')}
            </PrimaryButton>
          </div>
          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleCloseModal}>
              {t('cancel')}
            </PrimaryButton>
          </div>
        </Row>
      </Column>
    </Modal>
  );
};

export default CreateDepartmentModal;
